import React, {useMemo, useState} from 'react';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {debounce} from '../../../../../domain/lib/debounce';
import settingsParams from '../../../settingsParams';
import {useControllerProps} from '../../../../../domain/controllers/ControllerContext';
import {TextArea} from 'wix-ui-tpa';
import s from './BuyerNote.scss';
import {classes} from './BuyerNote.st.css';
import {useSettingsWithDefaults} from '../../../../../hooks/useSettingsWithDefaults';
import {TextButton, TextButtonPriority} from 'wix-ui-tpa/cssVars';
import {Document} from '@wix/wix-ui-icons-common';

export enum BuyerNoteDataHook {
  Root = 'BuyerNoteDataHook.root',
  Button = 'BuyerNoteDataHook.button',
  Body = 'BuyerNoteDataHook.body',
}

const AddNoteButton = ({handleClick, caption, isOpen}: {handleClick: () => void; caption: string; isOpen: boolean}) => {
  return (
    <TextButton
      data-hook={BuyerNoteDataHook.Button}
      priority={TextButtonPriority.primary}
      className={classes.textButton}
      prefixIcon={<Document />}
      aria-expanded={isOpen}
      onClick={handleClick}>
      {caption}
    </TextButton>
  );
};

const AddNoteBody = ({
  placeholder,
  handleChange,
  buyerNote,
}: {
  placeholder: string;
  handleChange: any;
  buyerNote: string;
}) => {
  const {t} = useTranslation();

  return (
    <div className={s.body}>
      <TextArea
        className={classes.textArea}
        data-hook={BuyerNoteDataHook.Body}
        placeholder={placeholder}
        autoFocus={true}
        maxLength={250}
        value={buyerNote || ''}
        onChange={handleChange}
        ariaLabel={t('cart.buyer_note_placeholder')}
      />
    </div>
  );
};

export const BuyerNote = () => {
  const {getSettingsWithDefaults} = useSettingsWithDefaults();
  const {updateBuyerNote, sendAddNoteBi} = useControllerProps().cartStore;
  const [buyerNote, setBuyerNote] = useState<string>(useControllerProps().cartStore.cartModel.buyerNote);
  const [isOpen, setIsOpen] = useState<boolean>(Boolean(buyerNote));
  const debouncedUpdateBuyerNote = useMemo(() => debounce(updateBuyerNote), [updateBuyerNote]);

  const handleChange = ({target: {value}}: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBuyerNote(value);
    return debouncedUpdateBuyerNote(value);
  };

  const handleOpen = () => {
    !isOpen && sendAddNoteBi();
    setIsOpen(!isOpen);
  };

  return (
    <div className={s.note} data-hook={BuyerNoteDataHook.Root}>
      <AddNoteButton
        isOpen={isOpen}
        handleClick={handleOpen}
        caption={getSettingsWithDefaults(settingsParams.CART_ADD_NOTE_LINK)}
      />
      {isOpen && (
        <AddNoteBody
          placeholder={getSettingsWithDefaults(settingsParams.CART_INSTRUCTIONS_TEXT)}
          handleChange={handleChange}
          buyerNote={buyerNote}
        />
      )}
    </div>
  );
};
